import styled from '@emotion/styled';
import { FC, useEffect, useRef, useState } from 'react';
import { VideoArtistAggregate } from '../../apiService';
import Carousel from '../../Carousel';
import { __t } from '../../i18n';
import { Box, Card, CardImage, Flex, Link, Paper, Row, Text } from '../../styles';
import { ResponsiveCols } from '../../styles/Row';
import { SpacingProps } from '../../withStyleSystem';
import Tooltip from '../../Tooltip';
import theme from '../../theme';

const ContentArtists: FC<ContentArtistsProps> = ({ artists }) => {
  const half = artists.length > 12 ? Math.ceil(artists.length / 2) : artists.length;
  const row1 = artists.slice(0, half);
  const row2 = artists.slice(half);

  return (
    <Carousel title={__t('content.stars')}>
      <ArtistRow artists={row1} mb={0} />
      <ArtistRow artists={row2} />
    </Carousel>
  );
};

const cols: ResponsiveCols = { md: 5, lg: 6, xl: 8 };

const ArtistRow: FC<ArtistRowProps> = ({ artists, ...spacing }) => {
  if (!artists.length) return null;

  return (
    <Row cols={cols} {...spacing}>
      {artists.map((artist) => (
        <ArtistCard key={artist.person.id} {...artist} />
      ))}
    </Row>
  );
};

const ArtistCard = ({ person, roles }: VideoArtistAggregate) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [openTooltip, setOpenTooltip] = useState(false);

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleHeaderTooltip();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleHeaderTooltip();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleHeaderTooltip = () => {
    setOpenTooltip(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  const { name, avatar_image, name_en } = person;
  const image = avatar_image?.path && avatar_image?.path + '?w=200';
  const href = `/artists/${person.id}/${person.slug}`;

  return (
    <Link ref={setReferenceElement} href={href} block>
      <CardContainer as={Paper} p={1}>
        <Box ml={1}>
          <Card orientation="square">{image && <CardImage src={image} alt={name} />}</Card>
        </Box>
        <Flex direction="column" justify="space-around">
          <Text variant="body3" as="p">
            {name}
          </Text>
          <Text variant="body3" as="p" weight="light" color="weak">
            {roles.join('، ')}
          </Text>
        </Flex>
      </CardContainer>
      <Tooltip
        disableMobileHover={openTooltip}
        anchor={referenceElement}
        type="text"
      >
        {name_en}
      </Tooltip>
    </Link>
  );
};

const CardContainer = styled(Flex)(({ theme }) => ({
  width: 196 / 16 + 'rem',
  borderRadius: theme.radius.medium,
  transition: 'background-color .3s ease',
  [`${Card}`]: {
    minWidth: 64 / 16 + 'rem',
    borderRadius: `0 ${theme.radius.default} ${theme.radius.default} 0`,
    backgroundImage: 'url(/static/images/placeholder/star.svg)',
  },
  '&:hover': {
    backgroundColor: theme.palette.background[600][0],
  },
  [theme.breakpoints.up('md')]: {
    width: 'auto',
  },
}));

interface ContentArtistsProps {
  artists: VideoArtistAggregate[];
}

type ArtistRowProps = ContentArtistsProps & SpacingProps;

export default ContentArtists;
